class InactivityTimeoutModalController {
    /**
     * Constructs an instance of InactivityTimeoutModalController.
     * 
     * @param {Object} $scope - AngularJS scope object.
     * @param {Object} $uibModalInstance - UI Bootstrap modal instance.
     * @param {Object} InactivityTimeoutService - Service for handling inactivity timeout.
     * @param {Object} TaskCountdownService - Service for managing task countdown.
     * @param {Object} TaskSelectionService - Service for task selection.
     * @param {Object} TaskDataService - Service for fetching task data.
     * @param {Object} $location - AngularJS $location service.
     * @param {Object} $config - Configuration service.
     * @param {Object} $timeout - AngularJS $timeout service.
     * @param {Object} $bridges - Service for handling bridges.
     * @param {number} lastTaskTime - Last task time.
     * @param {Object} UserInfoService - Service for fetching user information.
     * @param {Object} $signals - Service for handling signals.
     * @param {Object} $translate - AngularJS $translate service.
     * @param {Object} TaskInfoService - Service for fetching task information.
     * @param {Object} $filter - AngularJS $filter service.
     * @param {Object} $rootScope - AngularJS $rootScope service.
     * @param {Object} LogBridgeService - Service for logging.
     */
    constructor($scope, $uibModalInstance, InactivityTimeoutService, TaskCountdownService, TaskSelectionService, TaskDataService, $location, $config, $timeout, $bridges, lastTaskTime, UserInfoService, $signals, $translate, TaskInfoService, $filter, $rootScope, LogBridgeService) {
        this.$scope = $scope;
        this.$uibModalInstance = $uibModalInstance;
        this.InactivityTimeoutService = InactivityTimeoutService;
        this.TaskCountdownService = TaskCountdownService;
        this.TaskSelectionService = TaskSelectionService;
        this.TaskDataService = TaskDataService;
        this.$location = $location;
        this.$config = $config;
        this.$timeout = $timeout;
        this.$bridges = $bridges;
        this.lastTaskTime = lastTaskTime;
        this.UserInfoService = UserInfoService;
        this.$signals = $signals;
        this.$translate = $translate;
        this.TaskInfoService = TaskInfoService;
        this.$filter = $filter;
        this.$rootScope = $rootScope;
        this.LogBridgeService = LogBridgeService;

        // Initialize various properties and services
        this._taskCountdown = TaskCountdownService.getInstance();
        this._taskContext = TaskDataService.fetchContext();
        this._taskUUID = TaskSelectionService.getTaskUUID();
        this._task = this._taskContext.read(this._taskUUID);
        this._journalTime = $config.getJournalTime();
        this._candidateUUID = $bridges.authentication().getUserUUID();
        this._candidate = UserInfoService.getUserInfo(this._candidateUUID);
        this._existsJournal = this._task.hasJournal();
        this.$eventLog = $bridges.eventLog();

        /**
         * Blurring text
         */
        angular.element('#overview-view').addClass('blurred');
        angular.element('#task-view').addClass('blurred');

        // Initialize scope variables
        this.$scope.explanationMinChar = 0;
        this.$scope.eventExplanation = null;
        this.$scope.disabledRestartBtn = true;
        this.$scope.invigilatorUnlockCode = null;
        this.$scope.invigilatorUnlockCodeInputDisabled = false;
        this.$scope.showUnlock = false;

        // Format the last task time for display
        this.$scope.lastTaskTime = this._candidate.getTaskTimeFormatted(this._candidate.getTaskTime() - this.lastTaskTime);

        // Bind methods to the scope
        this.$scope.onCarryOnBtn = this.onCarryOnBtn.bind(this);
        this.$scope.isExpired = this.isExpired.bind(this);
        this.$scope.onShowUnlock = this.onShowUnlock.bind(this);
        this.$scope.onQuitBtn = this.onQuitBtn.bind(this);
        this.$scope.onRestartBtn = this.onRestartBtn.bind(this);
        this.$scope.onUnlockBtn = this.onUnlockBtn.bind(this);

        // Watch for changes in eventExplanation
        this.$scope.$watch('eventExplanation', this.watchEventExplanation.bind(this));

        /**
         * Log Inactivity Timeout Start
         */
        this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.INACTIVITY-TIMEOUT.LOG.INACTIVITY-TIMEOUT-START"), 'inactivity', '', '', {});

        // Bind and handle invigilator authentication signals
        this.onInvigilatorFailed = this.onInvigilatorFailed.bind(this);
        this.$signals.remove("tryInvigilatorAuthenticationFailed");
        this.$signals.on("tryInvigilatorAuthenticationFailed", this.onInvigilatorFailed);

        this.$signals.remove("tryInvigilatorAuthenticationSuccess");
        this.$signals.on("tryInvigilatorAuthenticationSuccess", this.onInvigilatorSuccess.bind(this));
    }

    /**
     * Handles the "Carry On" button click.
     * Resumes the inactivity timeout watch and closes the modal.
     */
    onCarryOnBtn() {
        this.InactivityTimeoutService.startWatch();
        this.$uibModalInstance.close();
        this.removeBlurredClass();
    }

    /**
     * Checks if the task countdown has expired.
     * 
     * @returns {boolean} - True if the task countdown has expired, otherwise false.
     */
    isExpired() {
        return this._taskCountdown.isExpired();
    }

    /**
     * Watches for changes in the event explanation input.
     * Enables or disables the restart button based on the input length.
     */
    watchEventExplanation() {
        if (this.$scope.eventExplanation === false || this.$scope.explanationMinChar === 0) {
            this.$scope.disabledRestartBtn = false;
            return false;
        }
        if (this.$scope.eventExplanation && this.$scope.eventExplanation.length >= this.$scope.explanationMinChar) {
            this.$timeout(() => {
                this.$scope.$apply(() => {
                    this.$scope.disabledRestartBtn = false;
                });
            }, 1);
        } else {
            this.$scope.disabledRestartBtn = true;
        }
    }

    /**
     * Handles the "Show Unlock" button click.
     * Sets up the unlock UI based on the type of action (quit or reset).
     * 
     * @param {string} type - The type of action (quit or reset).
     */
    onShowUnlock(type) {
        this.$scope.type = type;
        this.$scope.showUnlock = true;

        if (this.$scope.type === 'quit') {
            this.$scope.adminEventType = 'finishEarlyByInvigilator';
        } else if (this.$scope.type === 'reset') {
            this.$scope.adminEventType = 'inactivityResetByInvigilator';
        }

        const eventTypes = angular.copy(this.TaskInfoService.getTaskInfo().getEventTypes());
        const filteredEventType = this.$filter('filter')(eventTypes, { id: this.$scope.adminEventType }, true)[0];
        if (angular.isDefined(filteredEventType) && filteredEventType.explanatoryNote === 'mandatory') {
            this.$scope.explanationMinChar = 1;
            this.$scope.disabledRestartBtn = true;
        }
    }

    /**
     * Handles the "Quit" button click.
     * Logs the event and dismisses the modal.
     * 
     * @param {string} type - The type of action (invigilator or other).
     */
    onQuitBtn(type) {
        if (type === 'invigilator' && !this.isExpired()) {
            this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.INACTIVITY-TIMEOUT.LOG.FINISH-EARLY-BY-INVIGILATOR"), "finishEarlyByInvigilator", this.$scope.eventExplanation, '', {});
        }

        this.$uibModalInstance.dismiss('cancel');
        this.removeBlurredClass();

        this._taskCountdown.setTaskExpired();
        if (this._existsJournal) {
            this.$location.path('/journalOptIn/');
        } else {
            this._taskCountdown.resetTime(this._journalTime, this._journalTime, 'journal');
            this.$location.path('/taskdone/' + this.$bridges.authentication().getUserUUID());
        }
    }

    /**
     * Handles the "Restart" button click.
     * Logs the event, dismisses the modal, and restarts the task countdown.
     */
    onRestartBtn() {
        this.$eventLog.logEvent(this.$translate.instant("MODULES.ADMIN.INACTIVITY-TIMEOUT.LOG.INACTIVITY-TIMEOUT-RESET"), "restartInactivityTimeoutByInvigilator", this.$scope.eventExplanation, "restart", {});

        this.$uibModalInstance.dismiss('cancel');
        this.removeBlurredClass();

        this._taskCountdown.resetTime(this._candidate.getTaskTime(), this.lastTaskTime, 'task');
        this._taskCountdown.startMeasure();

        this.InactivityTimeoutService.startWatch();
    }

    /**
     * Handles the invigilator authentication failure.
     * Updates the UI and logs the event.
     */
    onInvigilatorFailed() {
        this.$rootScope.$apply(() => {
            this.$scope.invigilatorUnlockCodeInputDisabled = false;
            this.$scope.errorMessage = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED');

            const eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED');
            this.$eventLog.logEvent(eMsg, 'invLoginFailed', '', '', {});
            this.$scope.invigilatorUnlockCode = null;
        });
    }

    /**
     * Handles the invigilator authentication success.
     * Updates the UI and logs the event.
     * 
     * @param {string} data - The data received from the invigilator authentication.
     */
    onInvigilatorSuccess(data) {
        this.$rootScope.$apply(() => {
            const result = JSON.parse(data);
            this.$scope.invigilatorUnlockCodeInputDisabled = false;

            if (!angular.isObject(result) || result.type !== 'invigilator') {
                this.LogBridgeService.warn("Received invalid user data from invigilator login" + JSON.stringify(result));
                this.onInvigilatorFailed();
            } else if (result.type === 'invigilator') {
                this.$scope.invigilatorIsUnlocked = true;
                this.$signals.remove("tryInvigilatorAuthenticationFailed");
                this.$signals.remove("tryInvigilatorAuthenticationSuccess");

                const eMsg = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_SUCCESS');
                this.$eventLog.logEvent(eMsg, 'invLoginSuccess', '', '', {});
            }
        });
    }

    /**
     * Handles the "Unlock" button click.
     * Attempts to authenticate the invigilator and updates the UI accordingly.
     */
    onUnlockBtn() {
        this.$scope.errorMessage = null;
        if (!this.$scope.invigilatorUnlockCode) {
            this.$scope.errorMessage = this.$translate.instant('MODULES.ADMIN.EVENTLOG_INVIGILATOR_UNLOCK_FAILED');
        } else if (this.$scope.invigilatorUnlockCode) {
            this.$scope.invigilatorUnlockCodeInputDisabled = true;
            this.$bridges.authentication().tryInvigilatorAuthentication(this.$scope.invigilatorUnlockCode);
        }
    }

    /**
     * Removes the blurred class from the overview and task views.
     */
    removeBlurredClass() {
        angular.element('#overview-view').removeClass('blurred');
        angular.element('#task-view').removeClass('blurred');
    }
}

export default InactivityTimeoutModalController;